import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import Icon from './Icon'

/**
 * Notifications non bloquantes.
 *
 * Remplace les alert() : ceux-ci gèlent la page, ne disent pas quelle action a
 * réussi et ne permettent aucune action de rattrapage. Un toast peut porter un
 * bouton (« Annuler »), ce qui rend les suppressions réversibles.
 */
const ToastContext = createContext(null)

const DEFAULT_DURATION = 4000

const VARIANTS = {
  success: { icon: 'checkCircle', accent: 'text-positive' },
  error: { icon: 'alertCircle', accent: 'text-negative' },
  info: { icon: 'info', accent: 'text-accent' },
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timers = useRef(new Map())

  const dismiss = useCallback((id) => {
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
    setToasts((current) => current.filter((t) => t.id !== id))
  }, [])

  const push = useCallback((variant, message, options = {}) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    // Une action « Annuler » a besoin de plus de temps qu'un simple accusé de réception.
    const duration = options.duration ?? (options.action ? 8000 : DEFAULT_DURATION)
    setToasts((current) => [...current.slice(-3), { id, variant, message, action: options.action }])
    if (duration > 0) {
      timers.current.set(id, setTimeout(() => dismiss(id), duration))
    }
    return id
  }, [dismiss])

  const value = useMemo(() => ({
    success: (message, options) => push('success', message, options),
    error: (message, options) => push('error', message, options),
    info: (message, options) => push('info', message, options),
    dismiss,
  }), [push, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-24 z-[200] flex flex-col items-center gap-2 px-4 sm:bottom-6 sm:left-auto sm:right-6 sm:items-end"
        role="region"
        aria-label="Notifications"
      >
        {toasts.map((toast) => {
          const variant = VARIANTS[toast.variant] || VARIANTS.info
          return (
            <div
              key={toast.id}
              role="status"
              aria-live="polite"
              // Surface neutre plutôt qu'un aplat teinté : la couleur reste
              // sur l'icône, ce qui garde le texte parfaitement lisible.
              className="pointer-events-auto flex w-full animate-slide-up items-start gap-3 rounded-lg border border-line bg-surface px-4 py-3 shadow-lg sm:w-auto sm:min-w-[20rem] sm:max-w-md"
            >
              <Icon name={variant.icon} size={18} className={`mt-px ${variant.accent}`} />
              <p className="flex-1 break-words text-base text-ink">{toast.message}</p>
              {toast.action && (
                <button
                  type="button"
                  onClick={() => {
                    dismiss(toast.id)
                    toast.action.onClick()
                  }}
                  className="shrink-0 rounded-sm px-1.5 py-0.5 text-base font-medium text-accent transition-colors duration-fast hover:bg-accent-subtle"
                >
                  {toast.action.label}
                </button>
              )}
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                className="-mr-1 -mt-0.5 shrink-0 rounded-sm p-1 text-ink-muted transition-colors duration-fast hover:bg-surface-hover hover:text-ink"
                aria-label="Fermer la notification"
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    // Permet d'utiliser un composant hors provider (tests, pages isolées) sans planter.
    return { success: () => {}, error: () => {}, info: () => {}, dismiss: () => {} }
  }
  return context
}
